import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage, languages } from '../../contexts/LanguageContext';
import { useQuery } from '@tanstack/react-query';
import { getMe, getConversations } from '../../api/api';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LogOut, User as UserIcon, Menu, X, Sun, Moon, Search, Coins, MessageCircle, LayoutDashboard, ArrowRightLeft, Infinity as InfinityIcon, Package, PlusCircle, Archive } from 'lucide-react';
import { useState, useEffect } from 'react';
import LanguageSwitcher from '../LanguageSwitcher';
import { motion, AnimatePresence } from 'framer-motion';
import logoIcon from '../../imgs/1_trans.png';

import ImageWithFallback from '../common/ImageWithFallback';
import ScrollProgressBar from './ScrollProgressBar';

const Logo = () => {
  return (
    <div className="flex items-center gap-1 group cursor-pointer relative">
      <div className="relative flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 shrink-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-purple-500/30 to-secondary/30 blur-lg rounded-full opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative z-10 flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 bg-transparent rounded-full border-2 border-primary/60 group-hover:border-primary group-hover:scale-110 transition-all duration-500 shadow-md overflow-hidden p-0.5">
          <ImageWithFallback src={logoIcon} alt="Ahlafot Logo" className="w-full h-full object-contain drop-shadow-sm" />
        </div>
      </div>
    </div>
  );
};

export default function NavBar() {
  const { t, setLanguage, language, dir } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate();

  const { data: user } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: getMe,
    retry: false,
  });

  const { data: conversations = [] } = useQuery({
    queryKey: ['conversations'],
    queryFn: getConversations,
    enabled: !!user,
    refetchInterval: 10000,
  });

  const totalUnread = conversations.reduce((acc, conv) => {
    // ONLY count conversations that are visible in the Offers/Messages window
    const isVisible = conv.related_trade_id || conv.participants.some(p => p.includes('sona') || p.includes('support'));
    if (!isVisible) return acc;

    // Check if conversation is hidden in localStorage
    try {
        const hiddenConvoIds = JSON.parse(localStorage.getItem('hidden_conversations') || '[]');
        if (hiddenConvoIds.includes(conv._id)) return acc;
    } catch (e) {
        // Fallback if localStorage is corrupt
    }
    const count = conv.unread_count?.[user?.email] || 0;
    return acc + count;
  }, 0);

  const handleLogout = () => {
    localStorage.removeItem('swapx_user');
    window.location.reload();
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleThemeChange = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
        setIsSearchOpen(false);
        navigate(`/browse?keyword=${encodeURIComponent(e.target.value)}`);
    }
  };

  return (
    <>
      <ScrollProgressBar />
      <header className={`fixed top-0 left-0 right-0 z-[500] transition-all duration-300 ${isScrolled ? 'bg-background/95 backdrop-blur-md shadow-sm py-2' : 'bg-background py-3'}`}>
        <div className="w-full px-2 sm:px-8">
          <div className="flex items-center justify-between">
            
            {/* Start Group (Menu + Coins) */}
            <div className="flex items-center gap-1 sm:gap-4">
              {/* Burger Menu Button - Always at Start (Right in RTL, Left in LTR) */}
              <button 
                id="tour-menu"
                onClick={() => setIsOpen(!isOpen)} 
                className="p-2 text-muted-foreground hover:text-primary transition-colors flex flex-col items-center shrink-0"
              >
                <Menu size={26} className={isOpen ? 'rotate-90 transition-transform' : ''} />
                <span className="text-[10px] font-bold uppercase tracking-tighter mt-0.5">{t('menu', 'Menu')}</span>
              </button>

              {/* Coins - Moved next to Menu */}
              {user && (
                <Link 
                    to="/coins" 
                    id="tour-coins"
                    className="flex flex-col items-center p-1.5 sm:p-2.5 rounded-xl text-yellow-600 hover:bg-yellow-500/10 transition-colors shrink-0"
                >
                    <div className="flex items-center gap-1">
                        <Coins size={20} className="text-yellow-500" />
                        <span className="text-[12px] font-bold">{user.coins}</span>
                    </div>
                    <span className="text-[9px] sm:text-[10px] font-bold mt-0.5 uppercase tracking-tighter">{t('navCoins')}</span>
                </Link>
              )}
            </div>

            {/* Icons Group */}
            <div className="flex items-center gap-1 sm:gap-2">
              {user && (
                <Link 
                    to="/messages" 
                    id="tour-offers"
                    className="flex flex-col items-center p-2 sm:p-2.5 rounded-xl text-muted-foreground hover:bg-muted hover:text-indigo-500 transition-colors relative shrink-0"
                >
                    <div className="relative">
                        <ArrowRightLeft size={22} className="text-indigo-500" />
                        {totalUnread > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-full w-full bg-red-500"></span>
                            </span>
                        )}
                    </div>
                    <span className="text-[9px] sm:text-[10px] font-bold mt-0.5 uppercase tracking-tighter">{t('offers')}</span>
                </Link>
              )}



                <Link 
                    to="/create" 
                    id="tour-create"
                    className="flex flex-col items-center p-2 sm:p-2.5 rounded-xl text-emerald-500 hover:bg-emerald-500/10 transition-colors shrink-0"
                >
                    <PlusCircle size={22} />
                    <span className="text-[9px] sm:text-[10px] font-bold mt-0.5 uppercase tracking-tighter">{t('navCreate')}</span>
                </Link>

              {user && (
                <Link 
                    to="/my-items" 
                    id="tour-inventory"
                    className="flex flex-col items-center p-2 sm:p-2.5 rounded-xl text-blue-500 hover:bg-blue-500/10 transition-colors shrink-0"
                >
                    <Archive size={22} />
                    <span className="text-[9px] sm:text-[10px] font-bold mt-0.5 uppercase tracking-tighter">{t('navInventory', 'Inventory')}</span>
                </Link>
              )}

              <NavLink 
                  to="/browse" 
                  id="tour-explore"
                  className={({ isActive }) => `flex flex-col items-center p-2 sm:p-2.5 rounded-xl transition-all shrink-0 focus:outline-none ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-primary'}`}
              >
                  <Package size={22} />
                  <span className="text-[9px] sm:text-[10px] font-bold mt-0.5 uppercase tracking-tighter text-center leading-tight">
                      {t('navBrowse')}
                  </span>
              </NavLink>

              <button 
                  onClick={() => setIsSearchOpen(!isSearchOpen)} 
                  id="tour-search"
                  className={`flex flex-col items-center p-2 sm:p-2.5 rounded-xl transition-all shrink-0 focus:outline-none ${isSearchOpen ? 'bg-primary/10 text-slate-500' : 'text-muted-foreground hover:bg-muted hover:text-slate-500'}`}
              >
                  <Search size={22} className="text-slate-500" />
                  <span className="text-[9px] sm:text-[10px] font-bold mt-0.5 uppercase tracking-tighter text-center leading-tight">
                      {t('navSearch')}
                  </span>
              </button>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Logo - Always at End (Left in RTL, Right in LTR) */}
            <Link to="/" className="block shrink-0">
              <Logo />
            </Link>
          </div>
        </div>
      </header>

      {/* New Elegant Search Dropdown */}
      <AnimatePresence>
        {isSearchOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`fixed ${isScrolled ? 'top-[90px]' : 'top-[110px]'} left-0 right-0 px-4 py-2 z-[490] flex justify-center pointer-events-none transition-all duration-300`}
          >
            <div className="w-full max-w-md bg-background/80 backdrop-blur-md border border-border/50 shadow-xl rounded-2xl px-4 py-2 flex items-center pointer-events-auto">
              <Search size={18} className="text-muted-foreground mr-3" />
              <input 
                type="text" 
                placeholder={t('search')} 
                className="flex-1 bg-transparent border-none focus:outline-none text-sm font-bold py-1"
                autoFocus
                onKeyDown={handleSearch}
              />
              <button onClick={() => setIsSearchOpen(false)} className="ml-2 text-muted-foreground hover:text-foreground">
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Burger Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, x: dir === 'ltr' ? -100 : 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir === 'ltr' ? -100 : 100 }}
            className={`fixed inset-y-0 ${dir === 'ltr' ? 'left-0' : 'right-0'} w-72 bg-background border-x border-border shadow-2xl z-[650] p-6 flex flex-col gap-8`}
          >
            <div className="flex items-center justify-between mb-2">
                <Logo />
                <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-muted rounded-full transition-all"><X size={24}/></button>
            </div>

            <div className="flex flex-col gap-6">
                <div className="flex flex-wrap items-end justify-center gap-6 py-4 bg-muted/20 rounded-3xl">
                    {!user ? (
                        <>
                            <Link to="/login" onClick={() => setIsOpen(false)} className="flex flex-col items-center gap-1.5 group transition-all">
                                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-background border border-border group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all shadow-sm">
                                    <UserIcon size={22} />
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">{t('signIn')}</span>
                            </Link>
                            <Link to="/register" onClick={() => setIsOpen(false)} className="flex flex-col items-center gap-1.5 group transition-all">
                                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all">
                                    <InfinityIcon size={22} />
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-primary dark:text-primary-foreground/90 transition-colors">{t('register')}</span>
                            </Link>
                        </>
                    ) : (
                        <>
                            <Link to="/profile" id="tour-profile" onClick={() => setIsOpen(false)} className="flex flex-col items-center gap-1.5 group transition-all">
                                <span className="text-[11px] font-black text-foreground mb-1">{user.full_name}</span>
                                <div className="w-12 h-12 shrink-0">
                                    <ImageWithFallback src={user.avatar || `https://avatar.vercel.sh/${user.email}.svg`} alt="Avatar" className="w-full h-full rounded-2xl object-cover ring-2 ring-border group-hover:ring-primary transition-all shadow-md" />
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">{t('profile')}</span>
                            </Link>
                            {user?.role === 'admin' && (
                                <Link to="/admin" onClick={() => setIsOpen(false)} className="flex flex-col items-center gap-1.5 group transition-all">
                                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-background border border-border group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all shadow-sm">
                                        <LayoutDashboard size={22} />
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">{t('admin')}</span>
                                </Link>
                            )}
                        </>
                    )}
                </div>

                <div className="border-t border-border/50 pt-8 flex flex-wrap items-end justify-center gap-8">
                    <button onClick={handleThemeChange} className="flex flex-col items-center gap-1.5 group transition-all focus:outline-none">
                        <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-background border border-border group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all shadow-sm">
                            {theme === 'dark' ? <Sun size={22}/> : <Moon size={22}/>}
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">
                            {theme === 'dark' ? t('light') : t('dark')}
                        </span>
                    </button>
                    <LanguageSwitcher />
                </div>
            </div>

            {user && (
                <button onClick={handleLogout} className="mt-auto flex items-center justify-center gap-3 w-full py-4 rounded-2xl bg-red-500/10 text-red-500 font-black uppercase tracking-widest text-sm hover:bg-red-500 hover:text-white transition-all active:scale-95 shadow-lg shadow-red-500/5">
                    <LogOut size={20}/>
                    {t('logout')}
                </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {isOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[640]" onClick={() => setIsOpen(false)} />
      )}
    </>
  );
}
